/**
 * PATH: packages/ui/src/kanban/components/lane/inbox-filters/model.ts
 * FILE_ID: UI-KANBAN-INBOX-LANE-FILTER-MODEL-001
 * PURPOSE: Curated in-lane inbox filter state + persistence helpers.
 * WHY: Mimic Email Hub filters on the primary inbox lane without
 *   importing @quotech/email-hub or sharing Hub URL/rail storage keys.
 * MAIN EXPORTS: InboxLaneFilterState, load/save/clear, applyInboxLaneFilters
 * NON-RESPONSIBILITIES: Network fetch of email allow-lists (caller supplies).
 */
import type { Card, Lane } from '../../../types'

export type InboxReadFilter = 'all' | 'unread' | 'read'
export type InboxAllocationFilter = 'all' | 'allocated' | 'unallocated'

export interface InboxLaneFilterState {
  search: string
  isRead: InboxReadFilter
  allocation: InboxAllocationFilter
  /** Email category key; empty = all. */
  category: string
  /** Mailbox / address substring; empty = all. */
  mailbox: string
}

export const EMPTY_INBOX_LANE_FILTERS: InboxLaneFilterState = {
  search: '',
  isRead: 'all',
  allocation: 'all',
  category: '',
  mailbox: '',
}

/** Curated subset of Email Hub category keys for the lane strip. */
export const INBOX_LANE_CATEGORY_OPTIONS: { id: string; label: string }[] = [
  { id: '', label: 'All categories' },
  { id: 'bill', label: 'Bill' },
  { id: 'quote_request', label: 'Quote request' },
  { id: 'quote_submission', label: 'Quote submission' },
  { id: 'variation', label: 'Variation' },
  { id: 'invoice', label: 'Invoice' },
  { id: 'supplier', label: 'Supplier' },
  { id: 'general', label: 'General' },
  { id: 'marketing', label: 'Marketing' },
]

const storageKey = (boardId: string) => `kanban:inbox-lane-filters:${boardId}`

export function loadInboxLaneFilters(boardId: string): InboxLaneFilterState {
  if (typeof window === 'undefined' || !boardId) return { ...EMPTY_INBOX_LANE_FILTERS }
  try {
    const raw = window.localStorage.getItem(storageKey(boardId))
    if (!raw) return { ...EMPTY_INBOX_LANE_FILTERS }
    const parsed = JSON.parse(raw) as Partial<InboxLaneFilterState>
    return {
      search: typeof parsed.search === 'string' ? parsed.search : '',
      isRead:
        parsed.isRead === 'unread' || parsed.isRead === 'read'
          ? parsed.isRead
          : 'all',
      allocation:
        parsed.allocation === 'allocated' || parsed.allocation === 'unallocated'
          ? parsed.allocation
          : 'all',
      category: typeof parsed.category === 'string' ? parsed.category : '',
      mailbox: typeof parsed.mailbox === 'string' ? parsed.mailbox : '',
    }
  } catch {
    return { ...EMPTY_INBOX_LANE_FILTERS }
  }
}

export function saveInboxLaneFilters(
  boardId: string,
  state: InboxLaneFilterState,
): void {
  if (typeof window === 'undefined' || !boardId) return
  try {
    window.localStorage.setItem(storageKey(boardId), JSON.stringify(state))
  } catch {
    // Quota / private mode — filters stay session-local.
  }
}

export function inboxLaneFiltersActive(state: InboxLaneFilterState): boolean {
  return (
    state.search.trim() !== ''
    || state.isRead !== 'all'
    || state.allocation !== 'all'
    || state.category !== ''
    || state.mailbox.trim() !== ''
  )
}

export function applyInboxLaneFilters(
  cards: Card[],
  state: InboxLaneFilterState,
  ctx: {
    unseenByCard?: Record<string, number>
    /** When category filter is set, only keep cards whose primary_email
     *  is in this set. null = category filter not yet resolved (keep all). */
    categoryEmailIds?: Set<string> | null
  } = {},
): Card[] {
  const q = state.search.trim().toLowerCase()
  const mailbox = state.mailbox.trim().toLowerCase()

  return cards.filter((card) => {
    if (q) {
      const hay = [
        card.title,
        card.summary,
        card.primary_email_from_name,
        card.primary_email_from_address,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      if (!hay.includes(q)) return false
    }

    if (state.isRead !== 'all') {
      const unseen = ctx.unseenByCard?.[card.id] ?? 0
      if (state.isRead === 'unread' && unseen <= 0) return false
      if (state.isRead === 'read' && unseen > 0) return false
    }

    // Job allocation axis (Email Hub sense) — not "on a kanban card".
    if (state.allocation === 'allocated' && !card.job_id) return false
    if (state.allocation === 'unallocated' && card.job_id) return false

    if (mailbox) {
      const addr = (card.primary_email_from_address || '').toLowerCase()
      if (!addr.includes(mailbox)) return false
    }

    if (state.category) {
      if (ctx.categoryEmailIds == null) return true
      const emailId = card.primary_email
      if (!emailId || !ctx.categoryEmailIds.has(String(emailId))) return false
    }

    return true
  })
}

/** Query subset for Email Hub deep-link (no path — host builds mount). */
export function inboxLaneFilterQuery(
  state: InboxLaneFilterState,
): Record<string, string> {
  const q: Record<string, string> = {}
  if (state.search.trim()) q.search = state.search.trim()
  if (state.isRead === 'unread') q.is_read = 'false'
  if (state.isRead === 'read') q.is_read = 'true'
  if (state.allocation === 'allocated') q.allocation = 'allocated'
  if (state.allocation === 'unallocated') q.allocation = 'unallocated'
  if (state.category) q.category = state.category
  if (state.mailbox.trim()) {
    const n = Number(state.mailbox.trim())
    if (Number.isFinite(n) && n > 0 && String(n) === state.mailbox.trim()) {
      q.synced_by_account_id__in = String(n)
    } else {
      q.company = state.mailbox.trim().toLowerCase()
    }
  }
  return q
}

/**
 * The All read states / All allocation / All categories strip used to mount
 * on the primary inbox lane and sat in the same top band as the blue load
 * ribbon. Operators read that stack as a random glitch
 * (BUG-2026-09-24-KANBAN-BLUE-LOAD-RIBBON). Keep the predicate so callers
 * stay wired, and keep it off.
 */
export function shouldShowInboxLaneFilters(
  _lane: Lane,
  _lanes: Lane[],
  _boardKind?: string | null,
): boolean {
  return false
}
